import { type NMPLocale } from "@borealis/locale-common";
import { getNumberAsCurrencyFormatter, getNumberFormatter, type CurrencyCode } from "@borealis/number-format";
import { i18n } from "@lingui/core";
import IconChevronLeft16 from "@warp-ds/icons/react/chevron-left-16";
import IconChevronRight16 from "@warp-ds/icons/react/chevron-right-16";
import { Button, Modal, Pill } from "@warp-ds/react";
import React from "react";
import { createPortal } from "react-dom";
import { SemanticFilter, SemanticFilterItem } from "src/types/SemanticFilter";
import "./FiltersModal.css";
import { RangeFilter } from "./FiltersModal/filters/RangeFilter";
import { StandardFilter } from "./FiltersModal/filters/StandardFilter";

type FiltersModalProps = {
	open: boolean;
	onClose: () => void;
	filters: SemanticFilter[];
	isMobile?: boolean;
	onApply?: (selections: Record<string, unknown>) => void;
	onClear?: () => void;
	currency?: string;
	supportedFilterNames?: string[];
	initialActiveFilterName?: string;
	initialFocusValue?: unknown;
};

type FilterPill = { key: string; label: string; removable: boolean; onRemove?: () => void; focusValue?: unknown };

const srOnly = {
	position: "absolute" as const,
	width: 1,
	height: 1,
	padding: 0,
	margin: -1,
	overflow: "hidden" as const,
	clip: "rect(0, 0, 0, 0)" as const,
	whiteSpace: "nowrap" as const,
	border: 0,
};

const resolveNMPLocale = (locale: string): NMPLocale => {
	const normalized = locale.toLowerCase();
	const base = normalized.split("-")[0];
	if (base === "no") return "nb";
	if (base === "nb" || base === "sv" || base === "da" || base === "fi" || base === "en") {
		return base;
	}
	return "en";
};

export function FiltersModal({
	open,
	onClose,
	filters,
	isMobile,
	onApply,
	onClear,
	currency,
	supportedFilterNames,
	initialActiveFilterName,
	initialFocusValue,
}: FiltersModalProps) {
	const whitelisted = React.useMemo(() => {
		if (!supportedFilterNames || supportedFilterNames.length === 0) {
			return [];
		}
		return filters.filter((filter) => Boolean(filter.name) && supportedFilterNames.includes(filter.name as string));
	}, [filters, supportedFilterNames]);

	const allowedFilters = React.useMemo(() => {
		const list = whitelisted.length > 0 ? whitelisted : filters.filter((filter) => filter.name !== "q");
		return list;
	}, [filters, whitelisted]);
	const listIsEmpty = allowedFilters.length === 0;
	const [activeFilterName, setActiveFilterName] = React.useState<string>(allowedFilters[0]?.name ?? "");
	const [activeFilterFocusValue, setActiveFilterFocusValue] = React.useState<unknown>(undefined);
	const [showList, setShowList] = React.useState<boolean>(true);
	const activeFilter = React.useMemo(
		() => allowedFilters.find((filter) => filter.name === activeFilterName),
		[allowedFilters, activeFilterName],
	);
	const [activePathDepth, setActivePathDepth] = React.useState(0);
	const [activeParentLabel, setActiveParentLabel] = React.useState<string | undefined>(undefined);
	const [activeBackLabel, setActiveBackLabel] = React.useState<string | undefined>(undefined);
	const [activeBackHandler, setActiveBackHandler] = React.useState<(() => void) | undefined>(undefined);
	const listScrollRef = React.useRef<HTMLDivElement | null>(null);
	const desktopScrollRef = React.useRef<HTMLDivElement | null>(null);
	const desktopContentRef = React.useRef<HTMLDivElement | null>(null);
	const [isDesktopScrollable, setIsDesktopScrollable] = React.useState(false);
	const [animateIn, setAnimateIn] = React.useState(false);
	const [isVisible, setIsVisible] = React.useState(false);
	const exitTimeoutRef = React.useRef<number | null>(null);
	const handleBackChange = React.useCallback((handler?: () => void) => setActiveBackHandler(() => handler), []);
	const resolvedCurrency = currency && currency.trim() ? currency : "EUR";
	const resolvedLocale = React.useMemo(() => resolveNMPLocale(i18n.locale), [i18n.locale]);
	const currencyFormatter = React.useMemo(
		() =>
			getNumberAsCurrencyFormatter(resolvedLocale, {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			}),
		[resolvedLocale],
	);
	const numberFormatter = React.useMemo(
		() =>
			getNumberFormatter(resolvedLocale, {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			}),
		[resolvedLocale],
	);
	const formatCurrencyValue = React.useCallback(
		(value: number) => currencyFormatter(value, resolvedCurrency as CurrencyCode),
		[currencyFormatter, resolvedCurrency],
	);
	const formatNumberValue = React.useCallback((value: number) => numberFormatter(value), [numberFormatter]);
	const formatRangeValue = React.useCallback(
		(value: number, filter: SemanticFilter) => {
			if (filter.name === "price") {
				if (filter.unit) {
					return `${formatNumberValue(value)} ${filter.unit}`;
				}
				return formatCurrencyValue(value);
			}
			if (filter.unit) {
				return `${formatNumberValue(value)} ${filter.unit}`;
			}
			return formatNumberValue(value);
		},
		[formatCurrencyValue, formatNumberValue],
	);
	const isOnList = showList || !activeFilter;
	const titleText = isOnList
		? i18n.t({ id: "explore.searchInput.filters.title", message: "All filters" })
		: (activeFilter?.display_name ??
			activeFilter?.name ??
			i18n.t({ id: "explore.searchInput.filters.back", message: "Filter" }));
	const backLabel =
		activePathDepth > 0
			? (activeBackLabel ?? activeFilter?.display_name ?? activeFilter?.name ?? "Filter")
			: i18n.t({ id: "explore.searchInput.filters.title", message: "All filters" });
	const resolvedTitleText = !isOnList && activePathDepth > 0 && activeParentLabel ? activeParentLabel : titleText;
	const title = <span className="t3 truncate mt-8">{resolvedTitleText}</span>;
	const header = (
		<div className="grid grid-cols-[1fr_auto_1fr] items-start py-0 md:py-4 px-0 md:px-16">
			<div className="flex justify-start">
				{!isOnList && (
					<Button
						secondary
						quiet
						onClick={() => {
							if (activeBackHandler) {
								activeBackHandler();
							} else {
								setShowList(true);
								setActivePathDepth(0);
								setActiveParentLabel(undefined);
								setActiveBackLabel(undefined);
								setActiveBackHandler(undefined);
							}
						}}
						className="flex items-center gap-4 text-link t4 font-semibold bg-transparent appearance-none"
					>
						<IconChevronLeft16 aria-hidden="true" />
						<span className="truncate max-w-[140px] text-left">{backLabel}</span>
					</Button>
				)}
			</div>
			<div className="flex justify-center">{title}</div>
			<div className="flex justify-end items-start">
				<Button
					onClick={onClose}
					secondary
					quiet
					className="t4"
					aria-label={i18n.t({
						id: "explore.searchInput.filters.close",
						message: "Close filters",
					})}
				>
					{i18n.t({
						id: "explore.searchInput.filters.close",
						message: "Close filters",
					})}
				</Button>
			</div>
		</div>
	);
	const filtersSignature = React.useMemo(
		() =>
			allowedFilters
				.map(
					(f) => `${f.name ?? ""}:${f.type}:${f.filter_items?.length ?? 0}:${f.min_value ?? ""}:${f.max_value ?? ""}`,
				)
				.join("|"),
		[allowedFilters],
	);

	const buildInitialSelections = React.useCallback(() => {
		const initial: Record<string, unknown> = {};
		allowedFilters.forEach((filter) => {
			if (filter.type === "STANDARD_FILTER") {
				const defaults =
					filter.filter_items
						?.filter((item: SemanticFilterItem) => item.selected)
						.map((item: SemanticFilterItem) => item.value ?? item.name) ?? [];
				initial[filter.name ?? ""] = defaults;
			}
		});
		return initial;
	}, [allowedFilters]);

	const buildSelectionsFromUrl = React.useCallback(() => {
		if (typeof window === "undefined") return {};
		const params = new URLSearchParams(window.location.search);
		const fromUrl: Record<string, unknown> = {};

		const parseStandardValue = (raw: string, filter: SemanticFilter) => {
			const items: SemanticFilterItem[] = filter.filter_items ?? [];
			const numeric = Number(raw);
			if (!Number.isNaN(numeric)) {
				const numericMatch = items.find(
					(item: SemanticFilterItem) => typeof item.value === "number" && Number(item.value) === numeric,
				);
				if (numericMatch) return numeric;
			}
			const directMatch = items.find(
				(item: SemanticFilterItem) =>
					String(item.value ?? "") === raw ||
					String(item.name ?? "") === raw ||
					String(item.display_name ?? "") === raw,
			);
			if (directMatch) {
				return directMatch.value ?? directMatch.name ?? directMatch.display_name ?? raw;
			}
			return raw;
		};

		allowedFilters.forEach((filter) => {
			const name = filter.name ?? "";
			if (!name) return;

			if (filter.type === "STANDARD_FILTER") {
				const values = params.getAll(name);
				if (values.length === 0) return;
				const parsed = values.map((val) => parseStandardValue(val, filter)).filter((val) => val !== undefined);
				if (parsed.length > 0) {
					fromUrl[name] = parsed;
				}
				return;
			}

			if (filter.type === "RANGE_FILTER") {
				const fromName = filter.name_from ?? `${name}_from`;
				const toName = filter.name_to ?? `${name}_to`;
				const fromVal = params.get(fromName);
				const toVal = params.get(toName);
				if (fromVal === null && toVal === null) return;

				const min = fromVal !== null && !Number.isNaN(Number(fromVal)) ? Number(fromVal) : undefined;
				const max = toVal !== null && !Number.isNaN(Number(toVal)) ? Number(toVal) : undefined;
				if (min === undefined && max === undefined) return;
				fromUrl[name] = [min, max];
			}
		});

		return fromUrl;
	}, [allowedFilters]);

	const baseSelections = React.useMemo(() => buildInitialSelections(), [buildInitialSelections, filtersSignature]);
	const urlSelections = React.useMemo(() => buildSelectionsFromUrl(), [buildSelectionsFromUrl, filtersSignature]);

	const [initialSelections, setInitialSelections] = React.useState<Record<string, unknown>>(baseSelections);
	const [selections, setSelections] = React.useState<Record<string, unknown>>({ ...baseSelections, ...urlSelections });

	React.useEffect(() => {
		if (!open) return;
		const hasInitial =
			Boolean(initialActiveFilterName) && allowedFilters.some((filter) => filter.name === initialActiveFilterName);
		if (hasInitial) {
			setActiveFilterName(initialActiveFilterName as string);
			setShowList(false);
			setActiveFilterFocusValue(initialFocusValue);
		} else {
			setShowList(true);
			setActiveFilterFocusValue(undefined);
		}
		setActivePathDepth(0);
		setActiveParentLabel(undefined);
		setActiveBackLabel(undefined);
		setActiveBackHandler(undefined);
	}, [open, initialActiveFilterName, initialFocusValue, allowedFilters]);

	const scrollActiveFilterToTop = React.useCallback(() => {
		const scrollTarget = isMobile ? listScrollRef.current : desktopScrollRef.current;
		if (scrollTarget) {
			scrollTarget.scrollTop = 0;
		}
		if (isMobile && listScrollRef.current) {
			const dialog = listScrollRef.current.closest("[role='dialog']");
			const modalContent = dialog?.children?.[1] as HTMLElement | undefined;
			if (modalContent && modalContent !== scrollTarget) {
				modalContent.scrollTop = 0;
			}
		}
	}, [isMobile]);

	React.useEffect(() => {
		if (!showList) {
			requestAnimationFrame(scrollActiveFilterToTop);
		}
	}, [showList, activeFilterName, scrollActiveFilterToTop]);

	React.useEffect(() => {
		if (showList) {
			setActiveFilterFocusValue(undefined);
			setActivePathDepth(0);
			setActiveParentLabel(undefined);
			setActiveBackLabel(undefined);
		}
	}, [showList]);

	React.useEffect(() => {
		if (isMobile || typeof window === "undefined" || !isVisible) {
			setIsDesktopScrollable(false);
			return;
		}

		const scrollEl = desktopScrollRef.current;
		const contentEl = desktopContentRef.current;
		if (!scrollEl) return;

		const updateScrollState = () => {
			setIsDesktopScrollable(scrollEl.scrollHeight > scrollEl.clientHeight + 1);
		};

		updateScrollState();

		const win = window as Window;
		if (!("ResizeObserver" in win)) {
			win.addEventListener("resize", updateScrollState);
			return () => win.removeEventListener("resize", updateScrollState);
		}

		const observer = new ResizeObserver(updateScrollState);
		observer.observe(scrollEl);
		if (contentEl) {
			observer.observe(contentEl);
		}

		return () => observer.disconnect();
	}, [isMobile, isVisible, showList, activeFilterName, filtersSignature]);

	React.useEffect(() => {
		if (!activeFilterName && allowedFilters.length > 0) {
			setActiveFilterName(allowedFilters[0].name ?? "");
			setShowList(true);
			setActivePathDepth(0);
			setActiveParentLabel(undefined);
			setActiveBackHandler(undefined);
		}
	}, [activeFilterName, allowedFilters]);

	React.useEffect(() => {
		setInitialSelections(baseSelections);
		setSelections({ ...baseSelections, ...urlSelections });
		setActivePathDepth(0);
		setActiveParentLabel(undefined);
		setActiveBackHandler(undefined);
	}, [baseSelections, urlSelections, filtersSignature]);

	const handleSelectionChange = React.useCallback((name: string, value: unknown) => {
		setSelections((prev) => ({ ...prev, [name]: value }));
	}, []);

	const openFilter = React.useCallback((name: string, focusValue?: unknown) => {
		setActiveFilterName(name);
		setShowList(false);
		setActiveFilterFocusValue(focusValue);
		setActivePathDepth(0);
		setActiveParentLabel(undefined);
		setActiveBackHandler(undefined);
	}, []);

	const handleClear = () => {
		setSelections(initialSelections);
		onClear?.();
	};

	const handleApply = () => {
		onApply?.(selections);
		onClose();
	};

	const handleDismiss = () => {
		handleApply();
	};

	const resetActiveFilterSelection = () => {
		if (!activeFilter) return;
		const name = activeFilter.name ?? "";
		if (!name) return;
		const initial = initialSelections[name];

		if (activeFilter.type === "STANDARD_FILTER") {
			handleSelectionChange(name, Array.isArray(initial) ? initial : []);
		} else if (activeFilter.type === "RANGE_FILTER") {
			handleSelectionChange(name, [undefined, undefined]);
		}
		setActivePathDepth(0);
		setActiveParentLabel(undefined);
		setActiveBackHandler(undefined);
	};

	const handleUseCurrent = () => {
		setShowList(true);
		setActiveFilterFocusValue(undefined);
		setActivePathDepth(0);
		setActiveParentLabel(undefined);
		setActiveBackHandler(undefined);
		setActiveFilterName(allowedFilters[0]?.name ?? "");
	};

	const hasClearableSelections = React.useMemo(() => {
		return allowedFilters.some((filter) => {
			const name = filter.name ?? "";
			const current = selections[name];
			const initial = initialSelections[name];

			if (filter.type === "STANDARD_FILTER") {
				const currArr = Array.isArray(current) ? current : [];
				const initArr = Array.isArray(initial) ? initial : [];
				if (currArr.length !== initArr.length) return true;
				const currSet = new Set(currArr);
				return initArr.some((val) => !currSet.has(val));
			}

			if (filter.type === "RANGE_FILTER") {
				const currRange = Array.isArray(current) ? current : [];
				if (currRange.length !== 2) return false;
				const [currMinRaw, currMaxRaw] = currRange;
				const currMin = typeof currMinRaw === "number" && Number.isFinite(currMinRaw) ? currMinRaw : null;
				const currMax = typeof currMaxRaw === "number" && Number.isFinite(currMaxRaw) ? currMaxRaw : null;

				const initRange = Array.isArray(initial)
					? initial
					: [Number(filter.min_value ?? 0), Number(filter.max_value ?? 0)];
				if (initRange.length !== 2) return true;
				const [initMin, initMax] = initRange;

				return (currMin !== null && currMin !== initMin) || (currMax !== null && currMax !== initMax);
			}

			return false;
		});
	}, [allowedFilters, selections, initialSelections]);

	const hasActiveFilterSelection = React.useMemo(() => {
		if (!activeFilter) return false;
		const name = activeFilter.name ?? "";
		if (!name) return false;
		const current = selections[name];
		const initial = initialSelections[name];

		if (activeFilter.type === "STANDARD_FILTER") {
			const currArr = Array.isArray(current) ? current : [];
			const initArr = Array.isArray(initial) ? initial : [];
			if (currArr.length !== initArr.length) return true;
			const currSet = new Set(currArr);
			return initArr.some((val) => !currSet.has(val));
		}

		if (activeFilter.type === "RANGE_FILTER") {
			const currRange = Array.isArray(current) ? current : [];
			if (currRange.length !== 2) return false;
			const [currMinRaw, currMaxRaw] = currRange;
			const currMin = typeof currMinRaw === "number" && Number.isFinite(currMinRaw) ? currMinRaw : null;
			const currMax = typeof currMaxRaw === "number" && Number.isFinite(currMaxRaw) ? currMaxRaw : null;

			const initRange = Array.isArray(initial)
				? initial
				: [Number(activeFilter.min_value ?? 0), Number(activeFilter.max_value ?? 0)];
			if (initRange.length !== 2) return true;
			const [initMin, initMax] = initRange;

			return (currMin !== null && currMin !== initMin) || (currMax !== null && currMax !== initMax);
		}

		return false;
	}, [activeFilter, initialSelections, selections]);

	const findLabel = React.useCallback(
		(items: NonNullable<SemanticFilter["filter_items"]> | undefined, value: unknown): string | undefined => {
			if (!items) return undefined;
			for (const item of items) {
				const candidate = item.value ?? item.name ?? item.display_name;
				if (candidate === value) {
					return item.display_name ?? item.name ?? String(value);
				}
				if (Array.isArray(item.filter_items) && item.filter_items.length > 0) {
					const nested = findLabel(item.filter_items, value);
					if (nested) return nested;
				}
			}
			return undefined;
		},
		[],
	);

	const getFilterPills = React.useCallback(
		(filter: SemanticFilter): FilterPill[] => {
			const name = filter.name ?? "";
			if (!name) return [];

			if (filter.type === "STANDARD_FILTER") {
				const selectedValues = (selections[name] as unknown[]) ?? [];
				if (!Array.isArray(selectedValues) || selectedValues.length === 0) return [];

				const hasNested = (filter.filter_items ?? []).some(
					(item: SemanticFilterItem) => Array.isArray(item.filter_items) && item.filter_items.length > 0,
				);

				if (hasNested) {
					const selectedSet = new Set(selectedValues);
					const topItems: SemanticFilterItem[] = filter.filter_items ?? [];

					const getItemValue = (item: SemanticFilterItem) => item.value ?? item.name ?? item.display_name ?? "";
					const getItemLabel = (item: SemanticFilterItem) => item.display_name ?? item.name ?? String(item.value ?? "");

					const collectSubtreeValues = (item: SemanticFilterItem): unknown[] => {
						const values: unknown[] = [];
						const value = getItemValue(item);
						if (value !== "") values.push(value);
						const children: SemanticFilterItem[] = item.filter_items ?? [];
						if (children.length > 0) {
							children.forEach((child: SemanticFilterItem) => {
								values.push(...collectSubtreeValues(child));
							});
						}
						return values;
					};

					const parentSelections = topItems
						.map((item: SemanticFilterItem) => {
							const subtreeValues = collectSubtreeValues(item);
							const isSelected = subtreeValues.some((value) => selectedSet.has(value));
							if (!isSelected) return null;
							return {
								value: getItemValue(item),
								label: getItemLabel(item),
								subtreeValues,
							};
						})
						.filter(Boolean) as Array<{ value: unknown; label: string; subtreeValues: unknown[] }>;

					if (parentSelections.length === 0) return [];

					const maxVisible = 1;
					const visible: FilterPill[] = parentSelections.slice(0, maxVisible).map((selection, idx) => ({
						key: `${name}-parent-${String(selection.value)}-${idx}`,
						label: selection.label,
						removable: true,
						focusValue: selection.value,
						onRemove: () => {
							const selectionSet = new Set(selectedValues);
							selection.subtreeValues.forEach((value) => selectionSet.delete(value));
							handleSelectionChange(name, Array.from(selectionSet));
						},
					}));

					const remaining = parentSelections.length - visible.length;
					if (remaining > 0) {
						visible.push({
							key: `${name}-more`,
							label: `+${remaining}`,
							removable: false,
						});
					}
					return visible;
				}

				const selectionsWithLabels = selectedValues.map((val) => ({
					value: val,
					label: findLabel(filter.filter_items ?? [], val) ?? String(val),
				}));
				const maxVisible = 1;
				const visible: FilterPill[] = selectionsWithLabels.slice(0, maxVisible).map((selection, idx) => ({
					key: `${name}-${String(selection.value)}-${idx}`,
					label: selection.label,
					removable: true,
					focusValue: selection.value,
					onRemove: () => {
						const nextValues = selectedValues.filter((_, valueIdx) => valueIdx !== idx);
						handleSelectionChange(name, nextValues);
					},
				}));
				const remaining = selectionsWithLabels.length - visible.length;
				if (remaining > 0) {
					visible.push({
						key: `${name}-more`,
						label: `+${remaining}`,
						removable: false,
					});
				}
				return visible;
			}

			if (filter.type === "RANGE_FILTER") {
				const current = Array.isArray(selections[name])
					? (selections[name] as [number | undefined, number | undefined])
					: [];
				if (current.length !== 2) return [];

				const pills: FilterPill[] = [];
				const [currMinRaw, currMaxRaw] = current;

				const currMin = typeof currMinRaw === "number" && Number.isFinite(currMinRaw) ? currMinRaw : null;
				const currMax = typeof currMaxRaw === "number" && Number.isFinite(currMaxRaw) ? currMaxRaw : null;

				// Check if user has explicitly set a value (not null)
				const minSet = currMin !== null;
				const maxSet = currMax !== null;

				if (minSet || maxSet) {
					// Match SearchBar behavior: show appropriate label based on what's set
					let label: string;
					if (minSet && maxSet) {
						label = i18n.t(
							"explore.searchInput.filters.range.both",
							{
								min: formatRangeValue(currMin, filter),
								max: formatRangeValue(currMax, filter),
							},
							{
								// fallback for development when catalog is missing
								message: "{min}-{max}",
							},
						);
					} else if (minSet) {
						label = i18n.t(
							"explore.searchInput.filters.range.min",
							{ value: formatRangeValue(currMin, filter) },
							{
								// fallback for development when catalog is missing
								message: "From {value}",
							},
						);
					} else {
						label = i18n.t(
							"explore.searchInput.filters.range.max",
							{ value: formatRangeValue(currMax ?? 0, filter) },
							{
								// fallback for development when catalog is missing
								message: "Up to {value}",
							},
						);
					}

					// Reset clears the selection entirely (undefined means "not set")
					pills.push({
						key: `${name}-range`,
						label,
						removable: true,
						onRemove: () => handleSelectionChange(name, [undefined, undefined]),
					});
				}

				return pills;
			}

			return [];
		},
		[findLabel, handleSelectionChange, i18n, initialSelections, selections, formatRangeValue],
	);

	React.useEffect(() => {
		if (open) {
			if (exitTimeoutRef.current) {
				window.clearTimeout(exitTimeoutRef.current);
				exitTimeoutRef.current = null;
			}
			setIsVisible(true);
			// Trigger transition on next frame when opening
			requestAnimationFrame(() => setAnimateIn(true));
		} else {
			setAnimateIn(false);
			exitTimeoutRef.current = window.setTimeout(() => {
				setIsVisible(false);
				exitTimeoutRef.current = null;
			}, 200);
		}

		return () => {
			if (exitTimeoutRef.current) {
				window.clearTimeout(exitTimeoutRef.current);
				exitTimeoutRef.current = null;
			}
		};
	}, [open]);

	const contentClassName = isMobile ? "flex flex-col h-full min-h-0" : "flex flex-col";
	const listContainerClassName = isMobile
		? "flex-1 min-h-0 pr-4 space-y-16 transition-all duration-200"
		: "space-y-16 transition-all duration-200";
	const content = (
		<div className={contentClassName}>
			{listIsEmpty && (
				<p className="t4">
					{i18n.t({
						id: "explore.searchInput.filters.empty",
						message: "No filters available",
					})}
				</p>
			)}

			{!listIsEmpty && (
				<>
					<div className={listContainerClassName} ref={listScrollRef}>
						{showList && (
							<div className="overflow-hidden animate-fade-in bg-white">
								{allowedFilters.map((filter, idx) => {
									const showDivider = idx !== allowedFilters.length - 1;
									const labelText = `${filter.display_name ?? filter.name ?? "Filter"}${
										filter.name ? ` (${filter.name})` : ""
									}`;
									const pills = getFilterPills(filter);
									return (
										<div
											key={filter.name}
											onClick={(event) => {
												if ((event.target as HTMLElement | null)?.closest("[data-filter-pill]")) {
													return;
												}
												openFilter(filter.name ?? "");
											}}
											onKeyDown={(event) => {
												if (event.key === "Enter" || event.key === " ") {
													if ((event.target as HTMLElement | null)?.closest("[data-filter-pill]")) {
														return;
													}
													event.preventDefault();
													openFilter(filter.name ?? "");
												}
											}}
											role="button"
											tabIndex={0}
											className={`w-full min-h-[66px] flex items-center justify-between py-[16px] text-left bg-transparent appearance-none ${
												showDivider ? "border-b border-border-default" : ""
											}`}
										>
											<div className="flex items-center w-full gap-8">
												<span className="t4 font-semibold flex-none">{filter.display_name ?? filter.name}</span>
												<div className="flex items-center gap-4 ml-8 overflow-hidden">
													{pills.map((pill) => (
														<div key={pill.key} className="flex" data-filter-pill>
															<Pill
																suggestion
																className="max-w-[200px]"
																canClose={pill.removable}
																onClick={() => openFilter(filter.name ?? "", pill.focusValue)}
																onClose={
																	pill.removable && pill.onRemove
																		? () => {
																				pill.onRemove?.();
																			}
																		: undefined
																}
																label={
																	<span className="inline-block max-w-[160px] truncate align-middle">{pill.label}</span>
																}
															/>
														</div>
													))}
												</div>
												<IconChevronRight16 className="ml-auto shrink-0" aria-hidden="true" />
											</div>
											<span style={srOnly}>{labelText}</span>
										</div>
									);
								})}
							</div>
						)}

						{!showList && (
							<div className="space-y-16 animate-fade-in">
								{allowedFilters
									.filter((filter) => filter.name === activeFilterName)
									.map((filter, index) => {
										if (filter.type === "STANDARD_FILTER") {
											return (
												<StandardFilter
													key={filter.name ?? filter.display_name ?? index}
													filter={filter}
													index={index}
													selected={selections[filter.name ?? ""]}
													onChange={(next) => handleSelectionChange(filter.name ?? "", next)}
													onPathChange={(depth, parentLabel, backLabel) => {
														setActivePathDepth(depth);
														setActiveParentLabel(parentLabel);
														setActiveBackLabel(backLabel);
													}}
													onBackChange={handleBackChange}
													focusValue={activeFilterFocusValue}
												/>
											);
										}
										if (filter.type === "RANGE_FILTER") {
											return (
												<RangeFilter
													key={filter.name ?? filter.display_name ?? index}
													filter={filter}
													index={index}
													selected={selections[filter.name ?? ""]}
													onChange={(next) => handleSelectionChange(filter.name ?? "", next)}
													formatValue={(value) => formatRangeValue(value, filter)}
												/>
											);
										}
										return (
											<div key={filter.name ?? filter.display_name ?? index} className="space-y-4">
												<p className="t4 font-semibold">
													{filter.display_name ?? filter.name ?? "Filter"}
													{filter.name ? ` (${filter.name})` : ""}
												</p>
											</div>
										);
									})}
							</div>
						)}
					</div>
				</>
			)}
		</div>
	);

	const footerContent = showList ? (
		<div className="flex justify-end gap-12 w-full">
			{hasClearableSelections && (
				<Button
					className="min-w-[96px] flex-none border border-border-default bg-transparent text-ink"
					onClick={handleClear}
				>
					{i18n.t({
						id: "explore.searchInput.filters.clear",
						message: "Clear all",
					})}
				</Button>
			)}
			<Button
				primary
				fullWidth
				className={`min-w-[96px] ${hasClearableSelections ? "flex-1" : "w-full"} h-[48px] rounded-10 bg-[#d63a43] text-white border-transparent hover:bg-[#c3323b]`}
				onClick={handleApply}
			>
				{i18n.t({
					id: "explore.searchInput.filters.apply",
					message: "Apply",
				})}
			</Button>
		</div>
	) : (
		<div className="flex justify-end gap-12 w-full">
			{hasActiveFilterSelection && (
				<Button
					className="min-w-[96px] flex-none border border-border-default bg-transparent text-ink"
					onClick={resetActiveFilterSelection}
				>
					{i18n.t({
						id: "explore.searchInput.filters.clear.single",
						message: "Clear",
					})}
				</Button>
			)}
			<Button
				primary
				fullWidth
				className={`min-w-[96px] ${hasActiveFilterSelection ? "flex-1" : "w-full"} h-[48px] rounded-10 bg-[#d63a43] text-white border-transparent hover:bg-[#c3323b]`}
				onClick={handleUseCurrent}
			>
				{i18n.t({
					id: "explore.searchInput.filters.use",
					message: "Use",
				})}
			</Button>
		</div>
	);

	if (isMobile) {
		return (
			<Modal
				open={open}
				onDismiss={handleDismiss}
				title={header}
				left={false}
				right={false}
				footer={footerContent}
				className="filters-modal-backdrop flex items-end"
				style={{
					display: "flex",
					alignItems: "flex-end",
					justifyContent: "center",
				}}
			>
				{content}
			</Modal>
		);
	}

	// Desktop: render portal with overlay aligned to the right side of the viewport
	if (!isVisible) return null;

	return createPortal(
		<div
			className="filters-modal-backdrop fixed inset-0 z-50 flex justify-end pointer-events-auto"
			style={{
				backgroundColor: "rgba(0, 0, 0, 0.3)",
				opacity: animateIn ? 1 : 0,
				transition: "opacity 200ms ease",
			}}
			onMouseDown={handleDismiss}
		>
			<div
				className="h-full w-[425px] max-w-[90vw] bg-white shadow-xl border border-border-default rounded-tl-[4px] rounded-bl-[4px] flex flex-col pointer-events-auto overflow-hidden"
				style={{
					backgroundColor: "rgba(255, 255, 255, 1)",
					transform: animateIn ? "translateX(0)" : "translateX(12px)",
					opacity: animateIn ? 1 : 0,
					transition: "transform 200ms ease, opacity 200ms ease",
				}}
				onMouseDown={(e) => e.stopPropagation()}
			>
				{header}
				<div className="filters-scroll-area flex-1 overflow-y-auto" ref={desktopScrollRef}>
					<div className={`px-20 pt-16 ${isDesktopScrollable ? "pb-[80px]" : ""}`} ref={desktopContentRef}>
						{content}
					</div>
					<div className={`s-bg px-20 py-16 ${isDesktopScrollable ? "sticky bottom-0 z-10" : ""}`}>{footerContent}</div>
				</div>
			</div>
		</div>,
		document.body,
	);
}
